import React, { useEffect } from "react";

import {
  FaExclamationTriangle,
  FaTimes,
  FaTrash,
} from "react-icons/fa";

import {
  formatSubscriptionMoney,
  getAssociationName,
} from "../utils/subscriptionFormatters";

export default function DeleteSubscriptionModal({
  subscription,
  isDeleting = false,
  error = "",
  onCancel,
  onConfirm,
}) {
  const isOpen = Boolean(subscription);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (
        event.key === "Escape" &&
        !isDeleting
      ) {
        onCancel?.();
      }
    };

    document.addEventListener(
      "keydown",
      handleKeyDown,
    );

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener(
        "keydown",
        handleKeyDown,
      );

      document.body.style.overflow =
        previousOverflow;
    };
  }, [isOpen, isDeleting, onCancel]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/65 p-4 backdrop-blur-sm"
      role="presentation"
      onMouseDown={(event) => {
        if (
          event.target === event.currentTarget &&
          !isDeleting
        ) {
          onCancel?.();
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-subscription-title"
        className="w-full max-w-lg overflow-hidden rounded-3xl bg-white shadow-2xl dark:bg-slate-900"
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 p-5 dark:border-slate-800">
          <div className="flex items-start gap-3">
            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-red-100 text-xl text-red-600 dark:bg-red-950/50 dark:text-red-300">
              <FaExclamationTriangle />
            </div>

            <div>
              <h2
                id="delete-subscription-title"
                className="text-xl font-black text-slate-950 dark:text-white"
              >
                Confirmer la suppression
              </h2>

              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Cette opération supprimera
                définitivement cette ligne.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onCancel}
            disabled={isDeleting}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40 dark:hover:bg-slate-800 dark:hover:text-white"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <div className="p-5">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/60">
            <p className="font-black text-slate-950 dark:text-white">
              {getAssociationName(subscription)}
            </p>

            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
              {subscription.product}
            </p>

            <div className="mt-3 flex items-center justify-between gap-3 border-t border-slate-200 pt-3 text-sm dark:border-slate-700">
              <span className="text-slate-500 dark:text-slate-400">
                Référence #{subscription.id}
              </span>

              <strong className="text-slate-950 dark:text-white">
                {formatSubscriptionMoney(
                  subscription.total_amount,
                )}
              </strong>
            </div>
          </div>

          <div className="mt-4 rounded-2xl bg-red-50 p-4 text-sm leading-6 text-red-700 dark:bg-red-950/30 dark:text-red-300">
            Les statistiques et l’historique de
            chiffre d’affaires seront également
            recalculés après cette suppression.
          </div>

          {error && (
            <p className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
              {error}
            </p>
          )}
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-slate-100 p-5 dark:border-slate-800 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={isDeleting}
            className="rounded-2xl border border-slate-200 px-5 py-3 text-sm font-bold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            Annuler
          </button>

          <button
            type="button"
            onClick={onConfirm}
            disabled={isDeleting}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-red-600 px-5 py-3 text-sm font-black text-white transition hover:bg-red-700 disabled:cursor-wait disabled:opacity-60"
          >
            <FaTrash />

            {isDeleting
              ? "Suppression..."
              : "Supprimer définitivement"}
          </button>
        </div>
      </div>
    </div>
  );
}